import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { PageHeader, Modal, TableSkeletonRows, EmptyState, useToast, InlineSpinner, usePagination, Pagination } from '../components/UI';
import { TaeppTitle, TaeppPill, TaeppNote, SignatureInput } from '../components/TaeppUI';
import { fmtDate } from '../utils/format';

const fmtMoney = v => {
  const n = Number(v);
  return isNaN(n) ? '—' : `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const claimsLabel = r => `${r.claim_count} claim${r.claim_count === 1 ? '' : 's'}`;

// What the approver is deciding on, shown at the top of both dialogs.
function RequestSummary({ r }) {
  return (
    <div className="tp-req">
      <strong>{r.trainer_name}</strong>
      <span><i className="bi bi-calendar-range" aria-hidden="true" /> {fmtDate(r.date_from)} – {fmtDate(r.date_to)}</span>
      <span><i className="bi bi-files" aria-hidden="true" /> {claimsLabel(r)}</span>
      <span><i className="bi bi-cash-coin" aria-hidden="true" /> {fmtMoney(r.total_amount)}</span>
    </div>
  );
}

export default function TaeppQueuePage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('pending');
  const openedOnce = useRef(false);
  const [previewingId, setPreviewingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [saved, setSaved] = useState({ has_signature: false, image: null });
  const [signature, setSignature] = useState(null);   // data URL, '' = use saved, null = nothing yet
  const [rejectTarget, setRejectTarget] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/taepp/requests')
      .then(r => setRows(r.data.data || []))
      .catch(err => {
        if (err.response?.status === 403) setDenied(true);
        else toast('Failed to load TAEPP requests', 'error');
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (loading || openedOnce.current) return;
    openedOnce.current = true;
    if (!rows.some(r => r.status === 'pending') && rows.some(r => r.status === 'approved' && !r.uploaded_at)) setFilter('needs_upload');
  }, [loading, rows]);

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, []);

  const needsUpload = rows.filter(r => r.status === 'approved' && !r.uploaded_at);
  const filtered = filter === 'all' ? rows
    : filter === 'needs_upload' ? needsUpload
    : rows.filter(r => r.status === filter);
  const pendingCount = rows.filter(r => r.status === 'pending').length;
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(filtered);

  const preview = async id => {
    setPreviewingId(id);
    try {
      const res = await api.get(`/taepp/requests/${id}/preview`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch (err) {
      // A failed blob request returns its JSON error body as a Blob.
      let message = 'Failed to open the preview';
      try { message = JSON.parse(await err.response.data.text()).message || message; } catch { /* keep default */ }
      toast(message, 'error');
    } finally {
      setPreviewingId(null);
    }
  };

  // The trainer can cancel while this list is open. Check the request's current status
  // before showing a dialog, so a cancelled request never gets as far as the signature step.
  const stillPending = async row => {
    try {
      const r = await api.get(`/taepp/requests/${row.id}`);
      if (r.data.data.status === 'pending') return true;
      toast(r.data.data.message, 'error');
    } catch (err) {
      toast(err.response?.data?.message || 'Could not check this request. Try again.', 'error');
    }
    load();
    return false;
  };

  const openApprove = async row => {
    if (!await stillPending(row)) return;
    setApproveTarget(row);
    setSignature(null);
    try {
      const r = await api.get('/taepp/signature');
      setSaved(r.data.data);
    } catch {
      setSaved({ has_signature: false, image: null });
    }
  };
  const closeApprove = () => { if (!saving) setApproveTarget(null); };
  const canApprove = signature === '' ? saved.has_signature : !!signature;

  const approve = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/taepp/requests/${approveTarget.id}/approve`, signature ? { signature } : {});
      toast(r.data.message, r.data.emailed && r.data.uploaded ? 'success' : 'error');
      setApproveTarget(null);
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to approve', 'error');
      if (err.response?.status === 409) { setApproveTarget(null); load(); }
    } finally {
      setSaving(false);
    }
  };

  const reject = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/taepp/requests/${rejectTarget.id}/reject`, { remarks: remarks.trim() });
      toast(r.data.message, r.data.emailed ? 'success' : 'error');
      setRejectTarget(null);
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to reject', 'error');
      if (err.response?.status === 409) { setRejectTarget(null); load(); }
    } finally {
      setSaving(false);
    }
  };

  const retryUpload = async id => {
    setUploadingId(id);
    try {
      const r = await api.post(`/taepp/requests/${id}/retry-upload`);
      toast(r.data.message, 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to upload the signed PDF', 'error');
    } finally {
      setUploadingId(null);
      load();
    }
  };

  const resend = async id => {
    setResendingId(id);
    try {
      const r = await api.post(`/taepp/requests/${id}/resend`);
      toast(r.data.message, 'success');
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to resend the email', 'error');
    } finally {
      setResendingId(null);
    }
  };

  if (denied) {
    return (
      <>
        <PageHeader title="TAEPP Requests" subtitle="Approve or reject trainers' TAEPP requests" />
        <div className="sms-card"><EmptyState icon="bi-lock" message="Only the TAEPP approver can open this page." /></div>
      </>
    );
  }

  return (
    <div className="taepp">
      <PageHeader title="TAEPP Requests" subtitle={`${pendingCount} pending · approve with your e-signature or reject with remarks`} />

      {needsUpload.length > 0 && (
        <div className="tp-card mb-3" role="status">
          <i className="bi bi-cloud-slash" aria-hidden="true" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="tp-card-main">{needsUpload.length} approved TAEPP{needsUpload.length === 1 ? ' has' : 's have'} not been uploaded</div>
            <div className="tp-card-sub">The signed PDF was already emailed to the trainer. Retry the upload whenever you are ready.</div>
          </div>
          {filter !== 'needs_upload' && (
            <button type="button" className="tp-btn sm" onClick={() => setFilter('needs_upload')}>
              <i className="bi bi-list-ul" aria-hidden="true" />Show them
            </button>
          )}
        </div>
      )}

      <div className="sms-card mb-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <label htmlFor="taepp-filter" className="tp-label" style={{ margin: 0 }}><i className="bi bi-funnel" aria-hidden="true" /> Status</label>
          <select id="taepp-filter" className="sms-form-control" style={{ width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="needs_upload">Not uploaded</option>
            <option value="all">All</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? (
          <EmptyState icon="bi-file-earmark-check" message={filter === 'pending' ? 'No TAEPP requests are waiting for you' : filter === 'needs_upload' ? 'Every approved TAEPP has been uploaded' : 'No TAEPP requests found'} />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="sms-table">
                <thead><tr>
                  <th>Request</th><th>Trainer</th><th>Period</th><th className="num">Amount</th><th>Status</th><th>Requested</th><th>Details</th><th style={{ width: 250 }}>Actions</th>
                </tr></thead>
                <tbody>
                  {loading ? <TableSkeletonRows columns={8} /> : paged.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.request_no}</td>
                      <td style={{ fontWeight: 500 }}>{r.trainer_name}</td>
                      <td>{fmtDate(r.date_from)} – {fmtDate(r.date_to)}</td>
                      <td className="num">{fmtMoney(r.total_amount)}</td>
                      <td><TaeppPill status={r.status} /></td>
                      <td className="tp-cell-sub">{fmtDate(r.created_at)}</td>
                      <td className="tp-cell-sub" style={{ maxWidth: 240 }}>
                        {r.status === 'pending' && <>{claimsLabel(r)}{r.note ? ` · “${r.note}”` : ''}</>}
                        {r.status === 'approved' && (
                          <>
                            <div>{r.emailed_at ? `Emailed ${fmtDate(r.emailed_at)}` : 'Not emailed yet'}</div>
                            <div>{r.uploaded_at ? `Uploaded ${fmtDate(r.uploaded_at)}` : `Not uploaded yet${r.upload_error ? `: ${r.upload_error}` : ''}`}</div>
                          </>
                        )}
                        {r.status === 'rejected' && `Remarks: ${r.remarks || '—'}`}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <button type="button" className="tp-btn sm" disabled={previewingId === r.id} onClick={() => preview(r.id)}>
                            {previewingId === r.id ? <InlineSpinner /> : <><i className="bi bi-eye" aria-hidden="true" />Preview</>}
                          </button>
                          {r.status === 'pending' && (
                            <>
                              <button type="button" className="tp-btn sm primary" onClick={() => openApprove(r)}>
                                <i className="bi bi-check-lg" aria-hidden="true" />Approve
                              </button>
                              <button type="button" className="tp-btn sm text-bad" onClick={async () => { if (await stillPending(r)) { setRejectTarget(r); setRemarks(''); } }}>
                                <i className="bi bi-x-lg" aria-hidden="true" />Reject
                              </button>
                            </>
                          )}
                          {r.status === 'approved' && !r.uploaded_at && (
                            <button type="button" className="tp-btn sm" disabled={uploadingId === r.id} onClick={() => retryUpload(r.id)}>
                              {uploadingId === r.id ? <InlineSpinner /> : <><i className="bi bi-cloud-arrow-up" aria-hidden="true" />Retry upload</>}
                            </button>
                          )}
                          {r.status === 'approved' && !r.emailed_at && (
                            <button type="button" className="tp-btn sm" disabled={resendingId === r.id} onClick={() => resend(r.id)}>
                              {resendingId === r.id ? <InlineSpinner /> : <><i className="bi bi-envelope" aria-hidden="true" />Resend email</>}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>

      <Modal show={!!approveTarget} onClose={closeApprove}
        title={approveTarget ? <TaeppTitle icon="bi-patch-check" title={`Approve ${approveTarget.request_no}`} subtitle="Sign to approve. The signed PDF is emailed to the trainer." /> : ''}>
        {approveTarget && (
          <div className="taepp">
            <RequestSummary r={approveTarget} />
            <span className="tp-label">Your e-signature</span>
            <SignatureInput key={approveTarget.id + String(saved.has_signature)} hasSaved={saved.has_signature} savedImage={saved.image} onChange={setSignature} />
            <div style={{ marginTop: 14 }}>
              <TaeppNote icon="bi-envelope-check">Your signature is placed on the PDF, which is emailed to {approveTarget.trainer_name}. It is saved so you can reuse it next time.</TaeppNote>
            </div>
            <div className="tp-actions">
              <button type="button" className="tp-btn" onClick={closeApprove} disabled={saving}>Cancel</button>
              <button type="button" className="tp-btn primary" disabled={saving || !canApprove} onClick={approve}>
                {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" aria-hidden="true" />Approve and send</>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal show={!!rejectTarget} onClose={() => !saving && setRejectTarget(null)}
        title={rejectTarget ? <TaeppTitle icon="bi-x-octagon" title={`Reject ${rejectTarget.request_no}`} subtitle="Tell the trainer why. This is emailed to them." /> : ''}>
        {rejectTarget && (
          <div className="taepp">
            <RequestSummary r={rejectTarget} />
            <label htmlFor="taepp-remarks" className="tp-label">Remarks (required)</label>
            <textarea id="taepp-remarks" className="sms-form-control" rows={3} maxLength={500} style={{ width: '100%' }}
              placeholder="For example: the 14 Oct claim is missing."
              value={remarks} onChange={e => setRemarks(e.target.value)} />
            <div className="tp-hint" style={{ textAlign: 'right', marginTop: 4 }}>{remarks.length}/500</div>
            <div style={{ marginTop: 10 }}>
              <TaeppNote icon="bi-envelope">The remarks are emailed to {rejectTarget.trainer_name} and shown on their TAEPP page.</TaeppNote>
            </div>
            <div className="tp-actions">
              <button type="button" className="tp-btn" onClick={() => setRejectTarget(null)} disabled={saving}>Cancel</button>
              <button type="button" className="tp-btn danger" disabled={saving || !remarks.trim()} onClick={reject}>
                {saving ? <InlineSpinner /> : <><i className="bi bi-x-lg" aria-hidden="true" />Reject and send</>}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
